import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { SettingsNote } from "../../components/settings/SettingsList";
import appConfig from "../../config/appConfig";
import { getFingerprintHeaders, refreshAccessToken } from "../../helper/apiUtils";
import LanguageContext from "../../helper/LanguageContext";
import { $user } from "../../helper/UserFactory";
import { openCompanySettingsFor } from "../Cockpit/cockpitNavigation";
import "./CompanyAdmin.css";

// The Admin Panel's company admin pages, built by `npm run build:company-admin`
// (tools/company-admin) and served from public/.
const ADMIN_APP_URL = "/company-admin-app/index.html";
// Kjuup.io Abo only: a separate, much smaller bundle than the Admin-Bereich.
const KJUUP_IO_APP_URL = "/company-admin-app/kjuup-io.html";

let hostLanguage = "de";

function packageColor() {
  return getComputedStyle(document.documentElement).getPropertyValue("--kjuup-accent").trim();
}

// Read by the embedded app at start-up: it uses this session instead of an Admin Panel login.
window.__KJUUP_ADMIN_BRIDGE__ = {
  apiBaseUrl: appConfig.API_URL,
  getToken: () => $user.getToken(),
  refreshToken: () => refreshAccessToken(),
  getFingerprintHeaders: () => getFingerprintHeaders(),
  getUser: () => $user.get(),
  getLanguage: () => hostLanguage,
  getAccentColor: packageColor,
};

function embeddedApp(frame) {
  try {
    return (frame && frame.contentWindow && frame.contentWindow.__KAD__) || null;
  } catch (_) {
    return null;
  }
}

function adminTarget({ companyId, section, route }) {
  if (route) return route;
  return `/admin/companies/${companyId}${section ? `#${section}` : ""}`;
}

// Narrow columns: let room names wrap instead of pushing the table past the frame.
const COMPACT_CSS = [
  "[data-slot='table'] tr > :first-child { white-space: normal !important; overflow-wrap: anywhere; }",
  "[data-slot='table'] tr > :nth-child(2) { width: auto !important; }",
].join("\n");

// Room details inside a KjuupWA room shell: the host draws title and tabs.
const ROOM_SHELL_CSS = [
  "[data-kad-room-header] { display: none !important; }",
  "[data-kad-room-header] + [data-slot='tabs'] > [data-slot='tabs-list'] { display: none !important; }",
  // The chat pane gets its height from fillChatPane; its columns fill it.
  "[data-kad-chat-pane] { min-height: 0 !important; }",
  "[data-kad-chat-pane] > div { max-height: none !important; }",
].join("\n");

const CHAT_PANE_MIN = 360;

// Room shell: the chat pane reaches down to the bottom of the frame's container
// (the host card scrolls nothing then). Content below the pane keeps its room.
function fillChatPane(frame, doc) {
  const pane = doc.querySelector("[data-kad-chat-pane]");
  const host = frame.parentElement;
  const root = doc.getElementById("root");
  if (!pane || !host || !root) return;
  const style = window.getComputedStyle(host);
  const avail = host.clientHeight - parseFloat(style.paddingTop || 0) - parseFloat(style.paddingBottom || 0);
  const paneRect = pane.getBoundingClientRect();
  const below = Math.max(0, root.getBoundingClientRect().bottom - paneRect.bottom);
  const next = Math.max(CHAT_PANE_MIN, Math.floor(avail - paneRect.top - below));
  if (Math.abs(paneRect.height - next) > 1) pane.style.height = `${next}px`;
}

/**
 * onOpenRoom(roomId): room clicks are handed to the host instead of opening
 * inside the frame. onExit(): "back" on the frame's first page.
 */
export function CompanyAdminEmbed({ companyId, section = "", route = "", bare = false, flush = false, compact = false, roomShell = false, app = "admin", className = "", onReady, onOpenRoom, onExit }) {
  const { language, isGerm } = useContext(LanguageContext);
  const lang = language || (isGerm ? "de" : "en");
  const frameRef = useRef(null);
  const srcRef = useRef(null);
  const targetRef = useRef("");
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onOpenRoomRef = useRef(onOpenRoom);
  onOpenRoomRef.current = onOpenRoom;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const hasOpenRoom = !!onOpenRoom;
  const hasExit = !!onExit;

  // Read by the embedded bridge from window.frameElement.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    frame.__kadOpenRoom = hasOpenRoom ? (id) => onOpenRoomRef.current && onOpenRoomRef.current(id) : undefined;
    frame.__kadExit = hasExit ? () => onExitRef.current && onExitRef.current() : undefined;
    return () => {
      frame.__kadOpenRoom = undefined;
      frame.__kadExit = undefined;
    };
  }, [hasOpenRoom, hasExit]);

  if (!srcRef.current) {
    const params = new URLSearchParams({ companyId: String(companyId) });
    if (bare) params.set("chrome", "hidden");
    const initial = adminTarget({ companyId, section, route });
    if (route) params.set("route", route);
    const hash = !route && section ? `#${section}` : "";
    const url = app === "kjuup-io" ? KJUUP_IO_APP_URL : ADMIN_APP_URL;
    srcRef.current = `${url}?${params.toString()}${hash}`;
    targetRef.current = initial;
  }

  const go = (target) => {
    const app = embeddedApp(frameRef.current);
    if (!app || typeof app.navigate !== "function") return false;
    if (targetRef.current === target) return true;
    app.navigate(target);
    targetRef.current = target;
    return true;
  };

  useEffect(() => {
    hostLanguage = lang;
    const app = embeddedApp(frameRef.current);
    if (app) app.setLanguage(lang);
  }, [lang]);

  useEffect(() => {
    const app = embeddedApp(frameRef.current);
    if (app) app.setCompany(companyId);
  }, [companyId]);

  // App.js switches the package (and with it --kjuup-accent) via data-kjuup-package.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const app = embeddedApp(frameRef.current);
      if (app) app.setAccentColor(packageColor());
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-kjuup-package"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const target = adminTarget({ companyId, section, route });
    if (target === targetRef.current) return;
    go(target);
  }, [companyId, section, route]);

  // The host can paint its own shell first. Reveal the iframe once the
  // embedded section has drawn its header, not when the document merely loads.
  useEffect(() => {
    if (!onReadyRef.current) return undefined;
    const frame = frameRef.current;
    if (!frame) return undefined;
    let observer;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (onReadyRef.current) onReadyRef.current();
    };
    const check = () => {
      try {
        const doc = frame.contentDocument;
        if (doc && doc.querySelector("[data-kad-io-ready]")) finish();
      } catch (_) {}
    };
    const attach = () => {
      check();
      try {
        const doc = frame.contentDocument;
        const root = doc && doc.getElementById("root");
        if (!root || typeof MutationObserver === "undefined") return;
        if (observer) observer.disconnect();
        observer = new MutationObserver(check);
        observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-kad-io-ready"] });
      } catch (_) {}
    };
    frame.addEventListener("load", attach);
    attach();
    const fallback = window.setTimeout(finish, 8000);
    return () => {
      frame.removeEventListener("load", attach);
      if (observer) observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [companyId, section, route]);

  useEffect(() => {
    if (!bare) return undefined;
    const frame = frameRef.current;
    if (!frame) return undefined;
    let observer;
    const fit = () => {
      try {
        const doc = frame.contentDocument;
        const root = doc && doc.getElementById("root");
        if (!root || !doc.body) return;
        if (roomShell) fillChatPane(frame, doc);
        const next = Math.ceil(Math.max(root.scrollHeight, root.offsetHeight, doc.body.scrollHeight));
        if (next > 40) frame.style.height = `${next}px`;
      } catch (_) {}
    };
    const attach = () => {
      fit();
      try {
        const doc = frame.contentDocument;
        const root = doc && doc.getElementById("root");
        if (!root || typeof ResizeObserver === "undefined") return;
        if (observer) observer.disconnect();
        observer = new ResizeObserver(fit);
        observer.observe(root);
      } catch (_) {}
    };
    const flushPad = () => {
      if (!flush) return;
      try {
        const doc = frame.contentDocument;
        if (!doc || !doc.head) return;
        let style = doc.getElementById("kad-flush");
        if (!style) {
          style = doc.createElement("style");
          style.id = "kad-flush";
          doc.head.appendChild(style);
        }
        style.textContent = [
          "html[data-kad-chrome='hidden'], html[data-kad-chrome='hidden'] body, html[data-kad-chrome='hidden'] #root, html[data-kad-chrome='hidden'] main { background: transparent !important; }",
          "html[data-kad-chrome='hidden'] main > div { padding: 0 !important; background: transparent !important; }",
          "html[data-kad-chrome='hidden'] [data-slot='card'] { background: transparent !important; border: none !important; box-shadow: none !important; }",
          "html[data-kad-chrome='hidden'] [data-slot='card-content'] {",
          "  padding-top: 0 !important;",
          "  padding-left: 0 !important;",
          "  padding-right: 0 !important;",
          "  background: transparent !important;",
          "}",
          compact ? COMPACT_CSS : "",
          roomShell ? ROOM_SHELL_CSS : "",
        ].join("\n");
      } catch (_) {}
    };
    frame.addEventListener("load", attach);
    frame.addEventListener("load", flushPad);
    attach();
    flushPad();
    // Room shell: the container's height (window resize) sets the chat pane's.
    const hostObserver = roomShell && frame.parentElement && typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(fit)
      : null;
    if (hostObserver) hostObserver.observe(frame.parentElement);
    return () => {
      frame.removeEventListener("load", attach);
      frame.removeEventListener("load", flushPad);
      if (observer) observer.disconnect();
      if (hostObserver) hostObserver.disconnect();
    };
  }, [bare, flush, compact, roomShell, section, route]);

  return (
    <iframe
      ref={frameRef}
      className={`company-admin-frame ${className}`.trim()}
      src={srcRef.current}
      data-company-id={companyId}
      title="Admin-Bereich"
      scrolling="no"
      onLoad={() => {
        const target = adminTarget({ companyId, section, route });
        if (target !== targetRef.current) go(target);
        const app = embeddedApp(frameRef.current);
        if (!app) return;
        if (app.setLanguage) app.setLanguage(lang);
        if (app.setAccentColor) app.setAccentColor(packageColor());
      }}
    />
  );
}

/*
 * The Admin-Panel sections of a company (the app's Admin Center). They live in
 * the company settings (/company-setting/:id/:deptId) for Kjuup+ company admins:
 * a row opens its section (bare embed) in place and the URL hash names the
 * section (#dept-employees …), so deep links and browser back keep working.
 */
export const ADMIN_CENTER_SECTIONS = {
  info: { hash: "info", anchor: "", icon: "domain", labelKey: "settings_admin_info", de: "Informationen", en: "Information" },
  dept: {
    hash: "dept-employees",
    anchor: "dept-employees",
    icon: "group",
    labelKey: "settings_admin_departments",
    de: "Abteilungen und Mitarbeiter",
    en: "Departments & Employees",
    countKey: "employeeCount",
  },
  docs: {
    hash: "company-documents",
    anchor: "company-documents",
    icon: "folder_open",
    labelKey: "settings_admin_documents",
    de: "Firmendokumente",
    en: "Company documents",
    countKey: "documentCount",
  },
  rooms: { hash: "rooms", anchor: "rooms", icon: "door_open", labelKey: "settings_admin_rooms", de: "Projekte / Chats", en: "Projects / Chats" },
  articles: { hash: "articles", route: "/admin/articles", icon: "newspaper", labelKey: "admin_center_articles", de: "Artikel", en: "Articles" },
  addemp: {
    hash: "add-employees",
    anchor: "add-employees",
    icon: "person_add",
    labelKey: "settings_admin_add_employees",
    de: "Mitarbeitende hinzufügen",
    en: "Add Employees",
  },
  partners: {
    hash: "external-partners",
    anchor: "external-partners",
    icon: "handshake",
    labelKey: "settings_admin_project_partners",
    de: "Projektpartner hinzufügen",
    en: "Add project partners",
  },
  partnerco: {
    hash: "partner-companies",
    anchor: "partner-companies",
    icon: "business",
    labelKey: "settings_admin_partner_companies",
    de: "Partnerfirmen hinzufügen",
    en: "Add partner companies",
  },
  notif: { hash: "notifications", anchor: "notifications", icon: "notifications", labelKey: "settings_admin_notifications", de: "Benachrichtigungen", en: "Notifications" },
  trans: { hash: "auto-translation", anchor: "auto-translation", icon: "translate", labelKey: "settings_admin_translation", de: "Übersetzungssprachen", en: "Translation languages" },
  startup: { hash: "startup-screen", anchor: "startup-screen", icon: "devices", labelKey: "settings_admin_startup", de: "Startbildschirm", en: "Startup screen" },
  myspace: { hash: "self-chat", anchor: "self-chat", icon: "chat", labelKey: "settings_admin_my_space", de: "My Space", en: "My Space" },
  media: { hash: "media-export", anchor: "media-export", icon: "hard_drive", labelKey: "admin_center_media_export", de: "Medien-Exportserver", en: "Media export server" },
  // Approve Kjuup once for the company's Microsoft 365 organisation (every company admin).
  teams: { hash: "microsoft-teams", anchor: "microsoft-teams", icon: "groups", labelKey: "admin_center_microsoft_teams", de: "Microsoft Teams", en: "Microsoft Teams" },
};

// Old links into the Kjuup.io block: it has its own page now.
const KJUUP_IO_HASHES = ["kjuup-io-subscriptions", "kjuup-io", "kio"];

/**
 * Index groups and their rows (the app's indexGroups). `teams`: the viewer is a company
 * admin (ROLE_COMPANY_SUPER_ADMIN), the only role the backend lets approve Microsoft Teams.
 */
export function adminCenterGroups(company, mediaExportEnabled, { teams = false } = {}) {
  const isKjuup = !!(company && company.isKjuup);
  return [
    { key: "company", labelKey: "admin_center_group_company", de: "Firma", en: "Company", keys: ["info", "dept", "docs", "rooms", "articles"] },
    { key: "people", labelKey: "admin_center_group_people", de: "Personen hinzufügen", en: "Add people", keys: ["addemp", "partners", "partnerco"] },
    {
      key: "settings",
      labelKey: "admin_center_group_settings",
      de: "Einstellungen",
      en: "Settings",
      keys: [teams ? "teams" : null, "notif", "trans", "startup", isKjuup ? "myspace" : null, isKjuup && mediaExportEnabled ? "media" : null].filter(Boolean),
    },
  ];
}

export function adminSectionKey(value) {
  const v = String(value || "").replace(/^#/, "").trim();
  if (!v) return null;
  return Object.keys(ADMIN_CENTER_SECTIONS).find((key) => key === v || ADMIN_CENTER_SECTIONS[key].hash === v) || null;
}

// Only GET /user/cockpit tells whether the media export server is on; the cockpit
// passes it along and it is kept for reloads of the settings.
const mediaExportKey = (companyId) => `kjuup_admin_media_export_${companyId}`;

export function readMediaExportFlag(companyId) {
  try {
    return sessionStorage.getItem(mediaExportKey(companyId)) === "1";
  } catch (_) {
    return false;
  }
}

export function storeMediaExportFlag(companyId, enabled) {
  try {
    sessionStorage.setItem(mediaExportKey(companyId), enabled ? "1" : "0");
  } catch (_) {}
}

/**
 * /company-admin/:id (old links, bookmarks): the Admin-Bereich is part of the
 * company settings now, so this only forwards there (with the section and the
 * state the link carried).
 */
export default function CompanyAdmin({ match, location }) {
  const companyId = match.params.id;
  const history = useHistory();
  const dispatch = useDispatch();
  const section = ((location && location.hash) || "").replace(/^#/, "") || new URLSearchParams((location && location.search) || "").get("section") || "";
  const state = (location && location.state) || undefined;
  useEffect(() => {
    if (KJUUP_IO_HASHES.includes(section)) {
      history.replace(`/kjuup-io/${companyId}`);
      return;
    }
    openCompanySettingsFor(dispatch, history, { id: companyId }, { section, state, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  return null;
}

const ShellLine = ({ width }) => (
  <span className="kjuup-io-abo-shell-line kjuup-io-abo-shell-line--sized" style={{ "--shell-line-width": `${width}px` }} />
);

/**
 * Stand-in painted while the embed boots. It repeats the layout the embedded
 * page shows for its own loading state (the app's Kjuup.io settings: plan card,
 * plan tiles, seat quota, price card), so the handover is invisible.
 */
function KjuupIoAboShell() {
  return (
    <div className="kjuup-io-abo-shell" aria-hidden="true">
      <div className="kjuup-io-abo-shell-card kjuup-io-abo-shell-plan">
        <span className="kjuup-io-abo-shell-tile" />
        <div className="kjuup-io-abo-shell-plan-text">
          <ShellLine width={192} />
          <ShellLine width={256} />
        </div>
      </div>
      <div className="kjuup-io-abo-shell-cycles">
        <span className="kjuup-io-abo-shell-cycle" />
        <span className="kjuup-io-abo-shell-cycle" />
      </div>
      <div className="kjuup-io-abo-shell-card kjuup-io-abo-shell-quota">
        <div className="kjuup-io-abo-shell-row">
          <ShellLine width={176} />
          <ShellLine width={36} />
        </div>
        <span className="kjuup-io-abo-shell-bar" />
      </div>
      <div className="kjuup-io-abo-shell-card">
        {[
          [112, 80],
          [144, 32],
          [160, 0],
          [80, 112],
          [128, 96],
        ].map(([left, right], index) => (
          <div key={index} className="kjuup-io-abo-shell-row kjuup-io-abo-shell-price">
            <ShellLine width={left} />
            {right ? <ShellLine width={right} /> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Kjuup.io plan, seats and billing only — no other company admin sections.
 * No page header: the intro line, then the embedded Admin-Panel page
 * (the app's Kjuup.io settings) on white.
 */
export function KjuupIoAboPage({ match }) {
  const { strings, isGerm } = useContext(LanguageContext);
  const tr = (key, de, en) => strings[key] || (isGerm ? de : en);
  const [ready, setReady] = useState(false);

  return (
    <div className="company_section company_list setting_profile kjuup-io-abo-page">
      <div className="kjuup-io-abo-scroll">
        <div className="kjuup-io-abo-column">
          <SettingsNote className="kjuup-io-abo-intro">
            {tr(
              "cockpit_io_settings_sub",
              "Plan und Plätze, Zuweisung der Mitarbeitenden sowie Abrechnung und Rechnungen.",
              "Plan and seats, employee assignment, billing and invoices."
            )}
          </SettingsNote>
          <div className="kjuup-io-abo-body">
            {ready ? null : <KjuupIoAboShell />}
            <div className={ready ? undefined : "kjuup-io-abo-pending"}>
              <CompanyAdminEmbed
                companyId={match.params.id}
                app="kjuup-io"
                bare
                flush
                className="is-abo"
                onReady={() => setReady(true)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
